import React, { useState, useEffect } from 'react'
import { UserAdmin, UserCreateAdmin } from '../services/userService'

interface Props {
  isOpen: boolean
  onClose: () => void
  onSubmit: (payload: UserCreateAdmin, role: 'admin' | 'user') => Promise<UserAdmin>
}

export default function CreateUserModal({ isOpen, onClose, onSubmit }: Props) {
  const [form, setForm] = useState<UserCreateAdmin>({ username: '', email: '', full_name: '', password: '', is_active: true })
  const [passwordConfirm, setPasswordConfirm] = useState<string>('')
  const [role, setRole] = useState<'admin' | 'user'>('user')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string>('')

  useEffect(() => {
    if (!isOpen) return
    setForm({ username: '', email: '', full_name: '', password: '', is_active: true })
    setPasswordConfirm('')
    setRole('user')
    setSaving(false)
    setError('')
  }, [isOpen])

  if (!isOpen) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      setError('')
      if (!form.username.trim() || !form.full_name.trim() || !form.password.trim()) {
        setError('아이디, 이름, 비밀번호는 필수입니다.')
        return
      }
      if (form.password.trim().length < 6) {
        setError('비밀번호는 최소 6자 이상이어야 합니다.')
        return
      }
      if (form.password !== passwordConfirm) {
        setError('비밀번호와 비밀번호 확인이 일치하지 않습니다.')
        return
      }
      setSaving(true)
      // 신규 생성은 항상 활성 사용자로 생성 (요구사항: UI에서 체크박스 제거)
      await onSubmit({ ...form, is_active: true }, role)
      onClose()
    } catch (e: any) {
      const raw = (e?.message || '').toString()
      try {
        const parsed = JSON.parse(raw)
        const detail = (parsed && (parsed.detail || parsed.message))
        setError(detail ? String(detail) : '사용자 생성에 실패했습니다.')
      } catch {
        setError(raw || '사용자 생성에 실패했습니다.')
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">사용자 추가</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="닫기" disabled={saving}>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">아이디 <span className="text-red-500">*</span></label>
            <input
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-500"
              value={form.username}
              onChange={e => setForm({ ...form, username: e.target.value })}
              placeholder="예: admin"
              disabled={saving}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">이메일 <span className="text-gray-400 font-normal">(선택)</span></label>
            <input
              type="email"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-500"
              value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
              placeholder="예: user@example.com"
              disabled={saving}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">이름 <span className="text-red-500">*</span></label>
            <input
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-500"
              value={form.full_name}
              onChange={e => setForm({ ...form, full_name: e.target.value })}
              placeholder="예: 홍길동"
              disabled={saving}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">비밀번호 <span className="text-red-500">*</span></label>
            <input
              type="password"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-500"
              value={form.password}
              onChange={e => setForm({ ...form, password: e.target.value })}
              placeholder="최소 6자"
              disabled={saving}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">비밀번호 확인 <span className="text-red-500">*</span></label>
            <input
              type="password"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-500"
              value={passwordConfirm}
              onChange={e => setPasswordConfirm(e.target.value)}
              placeholder="비밀번호를 다시 입력하세요"
              disabled={saving}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">역할</label>
            <div className="flex gap-4 mt-1">
              <label className="inline-flex items-center text-sm text-gray-700">
                <input
                  type="radio"
                  className="mr-2"
                  value="user"
                  checked={role === 'user'}
                  onChange={() => setRole('user')}
                  disabled={saving}
                />
                사용자
              </label>
              <label className="inline-flex items-center text-sm text-gray-700">
                <input
                  type="radio"
                  className="mr-2"
                  value="admin"
                  checked={role === 'admin'}
                  onChange={() => setRole('admin')}
                  disabled={saving}
                />
                관리자
              </label>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              사용자는 생성 후 “권한”에서 페이지/프로젝트 권한을 추가로 설정하세요.
            </p>
          </div>

          {error && (
            <div className="mt-1 p-2 text-sm rounded border border-red-200 bg-red-50 text-red-700">
              {error}
            </div>
          )}

          <div className="pt-2 flex gap-3">
            <button type="button" onClick={onClose} disabled={saving} className="flex-1 px-4 py-2 border border-gray-300 rounded hover:bg-gray-50">취소</button>
            <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-900">{saving ? '생성 중...' : '생성'}</button>
          </div>
        </form>
      </div>
    </div>
  )
}


